import React from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

// --- Type Definitions ---
export interface IBannerV2Item {
  LinkTitle: string;
  Link: string;
}

export interface IBannerV2Widget {
  Title: string;
  Items: IBannerV2Item[];
}

// --- Props for the BannerV2Item component ---
interface BannerV2ItemProps {
  item: IBannerV2Widget;
  error: any;
  onChange: (updatedItem: IBannerV2Widget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const BannerV2Item: React.FC<BannerV2ItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Ensure Title is a string and Items is an array.
  const bannerTitle: string = typeof item.Title === "string" ? item.Title : "";
  const bannerItems: IBannerV2Item[] = Array.isArray(item.Items) ? item.Items : [];

  // Helper: if the parameter is an event, extract its target.value; otherwise, use it as a string.
  const getValue = (e: React.ChangeEvent<HTMLInputElement> | string): string =>
    typeof e === "string" ? e : e.target.value;

  // Handler for overall Title.
  const handleTitleChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newTitle = getValue(e);
    onChange({ ...item, Title: newTitle }, "Title");
  };

  // Handler for an individual navigation item.
  const handleItemChange = (
    index: number,
    field: keyof IBannerV2Item,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedItems = [...bannerItems];
    updatedItems[index] = { ...updatedItems[index], [field]: newValue };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // Add a new navigation link.
  const handleAddItem = () => {
    const newItem: IBannerV2Item = { LinkTitle: "", Link: "" };
    const updatedItems = [...bannerItems, newItem];
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // Remove a navigation link.
  const handleRemoveItem = (index: number) => {
    const updatedItems = bannerItems.filter((_, i) => i !== index);
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          BannerV2 Widget
        </Typography>

        {/* Overall Title Input */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={bannerTitle}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />

        {/* Navigation Links Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Navigation Links
        </Typography>
        {bannerItems.map((navItem, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #ddd",
              p: 2,
              borderRadius: "4px",
              mb: 2,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`LinkTitle-${index}`}
                  title="Link Title"
                  value={navItem.LinkTitle}
                  error={error?.[index]?.LinkTitle || ""}
                  onChange={(e) => handleItemChange(index, "LinkTitle", e)}
                  onBlur={() => onblur("LinkTitle", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <GenericInput
                  type="text"
                  name={`Link-${index}`}
                  title="Link"
                  value={navItem.Link}
                  error={error?.[index]?.Link || ""}
                  onChange={(e) => handleItemChange(index, "Link", e)}
                  onBlur={() => onblur("Link", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveItem(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
        >
          Add Navigation Link
        </Button>
      </CardContent>
    </Card>
  );
};

export default BannerV2Item;
